import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

type EventHandler = (payload?: any) => void;

interface EventBus {
  publish: (eventType: string, payload?: any) => void;
  subscribe: (eventType: string, handler: EventHandler) => void;
  unsubscribe: (eventType: string, handler: EventHandler) => void;
}

const EventContext = createContext<EventBus | null>(null);

export const useEventBus = (): EventBus => {
  const context = useContext(EventContext);
  if (!context) {
    throw new Error('useEventBus must be used within an EventProvider');
  }
  return context;
};

export const EventProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [handlers, setHandlers] = useState<{
    [eventType: string]: EventHandler[];
  }>({});

  const publish = useCallback(
    (eventType: string, payload?: any) => {
      console.log('publish', eventType, payload);
      if (handlers[eventType]) {
        handlers[eventType].forEach((handler) => handler(payload));
      }
    },
    [handlers]
  );

  const subscribe = useCallback((eventType: string, handler: EventHandler) => {
    setHandlers((prev) => ({
      ...prev,
      [eventType]: [...(prev[eventType] || []), handler],
    }));
  }, []);

  const unsubscribe = useCallback(
    (eventType: string, handler: EventHandler) => {
      setHandlers((prev) => ({
        ...prev,
        [eventType]: prev[eventType]?.filter((h) => h !== handler) || [],
      }));
    },
    []
  );

  const value = useMemo(() => ({ publish, subscribe, unsubscribe }), [publish, subscribe, unsubscribe]);
  return (
    <EventContext.Provider value={value}>
      {children}
    </EventContext.Provider>
  );
};
